import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Footer } from "../components/Footer";
import { LanguageToggle } from "../components/LanguageToggle";
import { CarnivalStatusBadge } from "../components/CarnivalStatusBadge";
import {
  CARNIVAL,
  CARNIVAL_ISLANDS,
  CARNIVAL_ISLAND_LABELS,
  CARNIVAL_HUB_META,
  CARNIVAL_UI,
  CARNIVAL_LAST_UPDATED,
  formatCarnivalDate,
} from "../lib/carnivalMeta";
import { useSeoTags } from "../lib/useSeoTags";

const SITE = "https://islandsdaily.com";

const readStoredLang = () => {
  try {
    const s = localStorage.getItem("ir_lang");
    if (s === "en" || s === "nl" || s === "es" || s === "pap" || s === "pap_aw") return s;
  } catch (_e) { /* ignore */ }
  return "en";
};

// The comparison view surfaces one row per island × its most-marquee
// event so a reader can see the whole Caribbean season at a glance
// and click through for the full local schedule. Deliberately kept
// short — the hub's job is discovery, not depth.
const marqueeFor = (islandKey) => {
  const p = CARNIVAL[islandKey];
  // Prefer the "grand-parade" / "gran-marcha" style headline event; fall
  // back to the first confirmed event, then just the first estimated.
  const preferredKeys = ["gran-marcha", "grand-parade", "carnival-monday"];
  for (const k of preferredKeys) {
    const hit = p.events.find((e) => e.key === k);
    if (hit) return hit;
  }
  return p.events.find((e) => e.status === "confirmed") || p.events[0];
};

const buildHubItemListSchema = (lang) => {
  const meta = CARNIVAL_HUB_META[lang] || CARNIVAL_HUB_META.en;
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: meta.h1,
    description: meta.description,
    numberOfItems: CARNIVAL_ISLANDS.length,
    itemListElement: CARNIVAL_ISLANDS.map((slug, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${SITE}/${slug}/carnival`,
      name: `${CARNIVAL_ISLAND_LABELS[slug]} Carnival 2027`,
    })),
    dateModified: CARNIVAL_LAST_UPDATED,
  };
};

export default function CarnivalHubPage() {
  const [lang, setLang] = useState(() => readStoredLang());

  useEffect(() => {
    const onLangChange = () => setLang(readStoredLang());
    window.addEventListener("ir_lang_changed", onLangChange);
    window.addEventListener("storage", onLangChange);
    return () => {
      window.removeEventListener("ir_lang_changed", onLangChange);
      window.removeEventListener("storage", onLangChange);
    };
  }, []);
  const handleLangChange = useCallback((next) => {
    try {
      localStorage.setItem("ir_lang", next);
      window.dispatchEvent(new Event("ir_lang_changed"));
    } catch (_e) { /* ignore */ }
    setLang(next);
  }, []);

  const seoConfigs = useMemo(() => {
    const canonical = `${SITE}/carnival`;
    const alternates = [
      { hreflang: "en",        href: canonical },
      { hreflang: "nl",        href: canonical },
      { hreflang: "es",        href: canonical },
      { hreflang: "pap",       href: canonical },
      { hreflang: "x-default", href: canonical },
    ];
    return {
      en:     { ...CARNIVAL_HUB_META.en,     canonical, alternates },
      nl:     { ...CARNIVAL_HUB_META.nl,     canonical, alternates },
      es:     { ...CARNIVAL_HUB_META.es,     canonical, alternates },
      pap:    { ...CARNIVAL_HUB_META.pap,    canonical, alternates },
      pap_aw: { ...CARNIVAL_HUB_META.pap_aw, canonical, alternates },
    };
  }, []);
  useSeoTags(lang, seoConfigs);

  const meta = CARNIVAL_HUB_META[lang] || CARNIVAL_HUB_META.en;
  const itemListSchema = buildHubItemListSchema(lang);
  const rows = CARNIVAL_ISLANDS.map((slug) => ({
    slug,
    label: CARNIVAL_ISLAND_LABELS[slug],
    payload: CARNIVAL[slug],
    marquee: marqueeFor(slug),
  }));

  return (
    <div className="min-h-screen bg-white" data-testid="carnival-hub">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListSchema) }}
        data-testid="carnival-hub-schema"
      />

      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[#EEEEEE]">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-[13px] text-[#6B7C82] hover:text-[#1A2E35]"
            data-testid="carnival-hub-back-link"
          >
            <ArrowLeft size={15} /> {CARNIVAL_UI.back_home[lang] || CARNIVAL_UI.back_home.en}
          </Link>
          <Link to="/" className="ml-auto font-serif-display text-[18px] leading-none">
            <span className="text-[#1A1A1A]">Islands</span>
            <span className="text-[#0AADA0]">Daily</span>
          </Link>
          <LanguageToggle lang={lang} onChange={handleLangChange} />
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <h1
          className="font-serif-display text-3xl sm:text-4xl text-[#1A2E35] mb-3"
          data-testid="carnival-hub-h1"
        >
          {meta.h1}
        </h1>
        <p className="text-[15px] text-[#3D4C52] leading-[1.7] max-w-3xl mb-2">
          {meta.intro}
        </p>
        <div className="text-[11px] text-[#6B7C82] mb-8" data-testid="carnival-hub-last-updated">
          {CARNIVAL_UI.last_updated[lang] || CARNIVAL_UI.last_updated.en}: {CARNIVAL_LAST_UPDATED}
        </div>

        <div className="overflow-x-auto" data-testid="carnival-hub-table">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="text-[10px] uppercase text-[#6B7C82] tracking-wider border-b border-[#EEEEEE]">
                <th className="py-3 pr-4">{CARNIVAL_UI.col_island[lang]  || CARNIVAL_UI.col_island.en}</th>
                <th className="py-3 pr-4">{CARNIVAL_UI.col_season[lang]  || CARNIVAL_UI.col_season.en}</th>
                <th className="py-3 pr-4">{CARNIVAL_UI.col_marquee[lang] || CARNIVAL_UI.col_marquee.en}</th>
                <th className="py-3 pr-4">{CARNIVAL_UI.col_date[lang]    || CARNIVAL_UI.col_date.en}</th>
                <th className="py-3 pr-4">{CARNIVAL_UI.col_status[lang]  || CARNIVAL_UI.col_status.en}</th>
                <th className="py-3 pr-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ slug, label, payload, marquee }) => (
                <tr
                  key={slug}
                  className="border-b border-[#F5F5F5] align-middle"
                  data-testid={`carnival-hub-row-${slug}`}
                >
                  <td className="py-4 pr-4 whitespace-nowrap text-[14px] font-medium text-[#1A2E35]">
                    {label}
                  </td>
                  <td className="py-4 pr-4 whitespace-nowrap text-[12px] text-[#6B7C82]">
                    {formatCarnivalDate(payload.season.start, lang).split(",")[0]}
                    {" – "}
                    {formatCarnivalDate(payload.season.end, lang).split(",")[0]}
                  </td>
                  <td className="py-4 pr-4 text-[13.5px] text-[#1A2E35]">
                    {marquee.labels[lang] || marquee.labels.en}
                  </td>
                  <td className="py-4 pr-4 whitespace-nowrap text-[13px] text-[#1A2E35]">
                    {marquee.date
                      ? formatCarnivalDate(marquee.date, lang)
                      : (CARNIVAL_UI.date_tbd[lang] || CARNIVAL_UI.date_tbd.en)}
                  </td>
                  <td className="py-4 pr-4 whitespace-nowrap">
                    <CarnivalStatusBadge
                      status={marquee.status}
                      source={marquee.status === "confirmed" ? marquee.confirmed_by : null}
                      lang={lang}
                    />
                  </td>
                  <td className="py-4 pr-2 whitespace-nowrap">
                    <Link
                      to={`/${slug}/carnival`}
                      className="text-[12.5px] text-[#0AADA0] hover:underline"
                      data-testid={`carnival-hub-link-${slug}`}
                    >
                      {CARNIVAL_UI.see_full_schedule[lang] || CARNIVAL_UI.see_full_schedule.en} →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>

      <Footer />
    </div>
  );
}
